/**
 * Auth API for Tankef
 *
 * Handles OAuth 2.0 authentication and password reset flows.
 * Uses Password Grant for login (as specified by backend).
 */

import { config, logger } from '@tankef/env';
import { oauthClient, authClient } from './client';
import { TokenManager } from './token-manager';
import type {
  LoginRequest,
  LoginResponse,
  PasswordResetRequest,
  PasswordResetResponse,
  PasswordResetVerifyRequest,
  PasswordResetVerifyResponse,
  PasswordResetConfirmRequest,
  PasswordResetConfirmResponse,
} from './types';

// Re-export types and token manager
export * from './types';
export { TokenManager } from './token-manager';
export { StorageCleaner } from './storage-cleaner';
export { oauthClient, authClient } from './client';

// OAuth client ID from env config.
// In dev the BE keeps `tankef-web-app` as a public client (no secret).
// In staging/prod the same client is configured as confidential and the
// BE requires `client_secret` on every /oauth/token request — the secret
// is injected at build time via VITE_OAUTH_CLIENT_SECRET. This is a
// stopgap until the FE migrates to authorization_code + PKCE + DPoP.
const OAUTH_CLIENT_ID = config.oauth.clientId || 'tankef-web-app';

function appendClientCredentials(params: URLSearchParams): URLSearchParams {
  params.set('client_id', OAUTH_CLIENT_ID);
  if (config.oauth.clientSecret) {
    params.set('client_secret', config.oauth.clientSecret);
  }
  return params;
}

export const authApi = {
  // ============================================================================
  // OAuth 2.0 Login
  // ============================================================================

  /**
   * Login con OAuth 2.0 Password Grant
   * POST /oauth/token
   *
   * @param data - Email, password y opcionalmente rememberMe
   * @returns Token response con access_token y refresh_token
   */
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    const params = appendClientCredentials(
      new URLSearchParams({
        grant_type: 'password',
        username: data.email,
        password: data.password,
      })
    );
    const requestedScopes = config.oauth.defaultScopes?.trim();
    if (requestedScopes) {
      params.set('scope', requestedScopes);
    }

    const response = await oauthClient.post<LoginResponse>('/oauth/token', params);

    TokenManager.saveTokens(
      response.data.access_token,
      response.data.refresh_token,
      response.data.expires_in,
      response.data.scope,
      data.rememberMe ?? false
    );

    return response.data;
  },

  /**
   * Refresh access token usando refresh_token
   * POST /oauth/token
   *
   * @returns Nueva respuesta de token
   * @throws Error si no hay refresh token disponible
   */
  refreshToken: async (): Promise<LoginResponse> => {
    const refreshToken = TokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const params = appendClientCredentials(
      new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      })
    );

    const response = await oauthClient.post<LoginResponse>('/oauth/token', params);

    TokenManager.updateTokens(
      response.data.access_token,
      response.data.refresh_token,
      response.data.expires_in,
      response.data.scope
    );

    return response.data;
  },

  /**
   * Logout - Revoca el refresh token en el servidor y limpia tokens locales
   */
  logout: async (): Promise<void> => {
    try {
      const refreshToken = TokenManager.getRefreshToken();
      if (refreshToken) {
        await oauthClient.post(
          '/oauth/revoke',
          appendClientCredentials(new URLSearchParams({ token: refreshToken })),
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
        );
      }
    } catch (error) {
      // Log revoke error but don't fail logout — clear tokens anyway
      logger.error('[authApi.logout] Token revocation failed:', error);
    } finally {
      TokenManager.clearTokens();
    }
  },

  // ============================================================================
  // Password Reset
  // ============================================================================

  /**
   * Step 1 — Solicitar OTP de password reset.
   * POST /api/v1/auth/password/reset
   *
   * Siempre retorna 200 con un mensaje anonimizado (anti-enumeration).
   * Canal por defecto: SMS si el usuario tiene teléfono, si no email.
   */
  requestPasswordReset: async (data: PasswordResetRequest): Promise<PasswordResetResponse> => {
    const response = await authClient.post<PasswordResetResponse>(
      '/api/v1/auth/password/reset',
      data
    );
    return response.data;
  },

  /**
   * Step 2 — Verificar OTP y obtener reset_session_token (TTL 15 min).
   * POST /api/v1/auth/password/reset/verify
   */
  verifyPasswordReset: async (
    data: PasswordResetVerifyRequest
  ): Promise<PasswordResetVerifyResponse> => {
    const response = await authClient.post<PasswordResetVerifyResponse>(
      '/api/v1/auth/password/reset/verify',
      data
    );
    return response.data;
  },

  /**
   * Step 3 — Confirmar nueva contraseña.
   * POST /api/v1/auth/password/reset/confirm
   */
  confirmPasswordReset: async (
    data: PasswordResetConfirmRequest
  ): Promise<PasswordResetConfirmResponse> => {
    const response = await authClient.post<PasswordResetConfirmResponse>(
      '/api/v1/auth/password/reset/confirm',
      data
    );
    return response.data;
  },

  // ============================================================================
  // Session Helpers
  // ============================================================================

  /**
   * Verifica si el usuario está autenticado con sesión válida
   */
  isAuthenticated: (): boolean => {
    return TokenManager.hasValidSession();
  },

  /**
   * Verifica si puede intentar renovar la sesión
   */
  canRefreshSession: (): boolean => {
    return TokenManager.canRefreshSession();
  },

  /**
   * Obtiene el access token actual (para uso en interceptors)
   */
  getAccessToken: (): string | null => {
    return TokenManager.getAccessToken();
  },

  /**
   * Verifica si el token está por expirar
   */
  isTokenExpiring: (): boolean => {
    return TokenManager.isAccessTokenExpired();
  },

  /**
   * Intenta renovar la sesión si es posible
   * @returns true si se renovó exitosamente, false si no
   */
  tryRefreshSession: async (): Promise<boolean> => {
    if (!TokenManager.canRefreshSession()) {
      return false;
    }

    try {
      await authApi.refreshToken();
      return true;
    } catch {
      TokenManager.clearTokens();
      return false;
    }
  },

  // ============================================================================
  // Email Validation (Smart Email Validation Feature)
  // ============================================================================

  /**
   * Submit email for validation
   * POST /api/v1/auth/email/submit
   *
   * @param data - Email to validate
   * @todo Implement once backend endpoint is available
   */
  submitEmail: async (data: {
    email: string;
  }): Promise<{
    message: string;
    resend_available_at?: string;
  }> => {
    const response = await authClient.post('/api/v1/auth/email/submit', data);
    return response.data;
  },

  /**
   * Verify email OTP code
   * POST /api/v1/auth/email/verify
   *
   * @param data - OTP code and email
   * @todo Implement once backend endpoint is available
   */
  verifyEmailOtp: async (data: {
    email: string;
    code: string;
  }): Promise<{
    verified: boolean;
    message: string;
  }> => {
    const response = await authClient.post('/api/v1/auth/email/verify', data);
    return response.data;
  },

  // ============================================================================
  // Phone Validation (Smart Phone Validation Feature)
  // ============================================================================

  /**
   * Submit phone for validation
   * POST /api/v1/auth/phone/submit
   *
   * @param data - Phone number and channel (sms/whatsapp)
   * @todo Implement once backend endpoint is available
   */
  submitPhone: async (data: {
    phone: string;
    channel: 'sms' | 'whatsapp';
  }): Promise<{
    message: string;
    masked_phone: string;
    resend_available_at?: string;
  }> => {
    const response = await authClient.post('/api/v1/auth/phone/submit', data);
    return response.data;
  },

  /**
   * Verify phone OTP code
   * POST /api/v1/auth/phone/verify
   *
   * @param data - OTP code and phone
   * @todo Implement once backend endpoint is available
   */
  verifyPhoneOtp: async (data: {
    phone: string;
    code: string;
  }): Promise<{
    verified: boolean;
    message: string;
  }> => {
    const response = await authClient.post('/api/v1/auth/phone/verify', data);
    return response.data;
  },

  /**
   * Resend phone OTP code
   * POST /api/v1/auth/phone/resend
   *
   * @param data - Phone and channel to resend to
   * @todo Implement once backend endpoint is available
   */
  resendPhone: async (data: {
    phone: string;
    channel: 'sms' | 'whatsapp';
  }): Promise<{
    message: string;
    resend_available_at?: string;
  }> => {
    const response = await authClient.post('/api/v1/auth/phone/resend', data);
    return response.data;
  },

  // ============================================================================
  // Password Management
  // ============================================================================

  /**
   * Update user password
   * PATCH /api/v1/auth/password
   *
   * @param data - Current and new password
   * @todo Implement once backend endpoint is available
   */
  updatePassword: async (data: {
    current_password: string;
    new_password: string;
    password_confirmation: string;
  }): Promise<{
    message: string;
  }> => {
    const response = await authClient.patch('/api/v1/auth/password', data);
    return response.data;
  },
};
